import { useCallback, useContext } from 'react';
import { isNil, toString } from 'lodash';
import { useNavigate } from 'react-router-dom';
import { Allocation, CapTableDetailsModel, Company, FirmsService } from 'api';
import { allocationsAction, captableAction, companiesAction, globalAction } from 'common/actions';
import { firmSummaryUrl } from 'common/config/urls';
import { useStore } from 'common/store';
import { FirmCompany } from 'common/types/company';
import { DialogContextValues } from 'common/types/dialogContext';
import { UseStoreValues } from 'common/types/store';
import { ConfirmationDelete, ConfirmationDialog } from 'components/Dialogs';
import { DialogContext } from 'context';
import { FUND_INVESTMENTS_TITLE_SINGULAR } from 'pages/FundInvestments/common/constants';
import { getFundInvestmentsSummaryUrl } from 'pages/FundInvestments/common/urls';
import { FundInvestmentFormSchema } from 'pages/FundInvestments/hooks/useFundInvestmentForm';
import {
  useCreateFundInvestment,
  useDeleteFundInvestment,
  useUpdateFundInvestment,
} from 'services/hooks/fundInvestments';
import { useDialogFormsStore } from 'store';
import { getNumberValue, getObjectValue, getStringValue } from 'utilities';
import { UseFundInvestmentParams, UseFundInvestmentReturn } from './useFundInvestment.types';

const DELETE_LABEL = 'Delete';
const DIALOG_TITLE = 'Confirmation';

const useFundInvestment = (params: UseFundInvestmentParams): UseFundInvestmentReturn => {
  const { firmSlugParam } = params;

  const [storeValue, dispatch] = useStore() as UseStoreValues;
  const { companyInfo: currentFundInvestment, firmInfo: currentFirm } = getObjectValue(storeValue);
  const { id: fundInvestmentId, name: fundInvestmentName } = getObjectValue(currentFundInvestment);
  const { id: currentFirmId } = getObjectValue(currentFirm);

  const { showDialog } = useContext(DialogContext) as unknown as DialogContextValues;

  const navigate = useNavigate();

  const { mutateAsync: createFundInvestment } = useCreateFundInvestment();
  const { mutateAsync: updateFundInvestment } = useUpdateFundInvestment();
  const { mutateAsync: deleteFundInvestment } = useDeleteFundInvestment();

  const setDisplayFundInvestmentDialogForm = useDialogFormsStore(state => state.setDisplayFundInvestmentDialogForm);
  const setShouldUpdateFundInvestment = useDialogFormsStore(state => state.setShouldUpdateFundInvestment);

  const updateCompanyList = useCallback(async () => {
    if (!isNil(currentFirmId)) {
      const updatedCompanyList = (await FirmsService.apiFirmsCompaniesList(
        toString(currentFirmId)
      )) as unknown as FirmCompany[];

      if (updatedCompanyList) {
        dispatch(companiesAction.setCompanyList(updatedCompanyList));
      }
    }
  }, [currentFirmId, dispatch]);

  const updateCompanyInfo = useCallback(
    (updatedFundInvestment: Company) => {
      if (updatedFundInvestment) {
        dispatch(companiesAction.setCompanyInfo({ ...currentFundInvestment, ...updatedFundInvestment }));
      }
    },
    [currentFundInvestment, dispatch]
  );

  const closeFundInvestmentDialog = useCallback(() => {
    setDisplayFundInvestmentDialogForm(false);
    setShouldUpdateFundInvestment(false);
  }, [setDisplayFundInvestmentDialogForm, setShouldUpdateFundInvestment]);

  // Create
  const handleCreateFundInvestment = useCallback(
    async (fundInvestment: FundInvestmentFormSchema) => {
      dispatch(globalAction.showLoadingProgress(true));

      const createFundInvestmentResponse = await createFundInvestment(
        {
          fundInvestment: {
            ...fundInvestment,
            firm: getNumberValue(currentFirmId),
            type: Company.type.INVESTMENT,
          },
        },
        {
          onError: () => {
            dispatch(globalAction.showLoadingProgress(false));
          },
        }
      );

      const { fundInvestment: createdFundInvestment } = getObjectValue(createFundInvestmentResponse);

      if (!isNil(createdFundInvestment?.firm)) {
        // Update company list immediately after creation
        await updateCompanyList();

        // Reset captable and allocation info
        dispatch(captableAction.setCaptableInfo({} as CapTableDetailsModel));
        dispatch(allocationsAction.setAllocationInfo({} as Allocation));

        // Close fund investment dialog form
        closeFundInvestmentDialog();
      }

      dispatch(globalAction.showLoadingProgress(false));

      return navigate(
        getFundInvestmentsSummaryUrl(getStringValue(firmSlugParam), getStringValue(createdFundInvestment.slug))
      );
    },
    [
      closeFundInvestmentDialog,
      createFundInvestment,
      currentFirmId,
      dispatch,
      firmSlugParam,
      navigate,
      updateCompanyList,
    ]
  );

  // Update
  const handleUpdateFundInvestment = useCallback(
    async (fundInvestment: FundInvestmentFormSchema) => {
      dispatch(globalAction.showLoadingProgress(true));

      const updateFundInvestmentResponse = await updateFundInvestment(
        {
          fundInvestment: {
            ...fundInvestment,
            firm: getNumberValue(currentFirmId),
            id: getNumberValue(fundInvestmentId),
          },
        },
        {
          onError: () => {
            dispatch(globalAction.showLoadingProgress(false));
          },
        }
      );

      const { fundInvestment: updatedFundInvestment } = getObjectValue(updateFundInvestmentResponse);

      if (!isNil(updatedFundInvestment?.firm)) {
        // Update company list immediately after update
        await updateCompanyList();

        // Update company info immediately after update
        updateCompanyInfo(updatedFundInvestment);

        // Close fund investment dialog form
        closeFundInvestmentDialog();
      }

      dispatch(globalAction.showLoadingProgress(false));

      return navigate(
        getFundInvestmentsSummaryUrl(getStringValue(firmSlugParam), getStringValue(updatedFundInvestment.slug))
      );
    },
    [
      closeFundInvestmentDialog,
      currentFirmId,
      dispatch,
      firmSlugParam,
      fundInvestmentId,
      navigate,
      updateCompanyInfo,
      updateCompanyList,
      updateFundInvestment,
    ]
  );

  // Delete
  const handleDeleteFundInvestment = useCallback(async () => {
    dispatch(globalAction.showLoadingProgress(true));

    const deleteFundInvestmentResponse = await deleteFundInvestment(
      {
        fundInvestmentId,
      },
      {
        onError: () => {
          dispatch(globalAction.showLoadingProgress(false));
        },
      }
    );

    const { fundInvestmentId: deletedFundInvestmentId } = getObjectValue(deleteFundInvestmentResponse);

    if (!isNil(deletedFundInvestmentId)) {
      // Update company list immediately after deletion
      await updateCompanyList();

      // Close fund investment dialog form
      closeFundInvestmentDialog();
    }

    dispatch(globalAction.showLoadingProgress(false));

    return navigate(firmSummaryUrl(getStringValue(firmSlugParam)));
  }, [
    closeFundInvestmentDialog,
    deleteFundInvestment,
    dispatch,
    firmSlugParam,
    fundInvestmentId,
    navigate,
    updateCompanyList,
  ]);

  const confirmDeleteFundInvestment = useCallback(() => {
    showDialog({
      wrapper: ConfirmationDialog,
      content: () => <ConfirmationDelete itemName={`${FUND_INVESTMENTS_TITLE_SINGULAR}: ${fundInvestmentName}`} />,
      actions: [
        {
          label: DELETE_LABEL,
          variant: 'contained',
          type: 'danger',
          callback: handleDeleteFundInvestment,
        },
      ],
      wrapperProps: {
        title: DIALOG_TITLE,
      },
    });
  }, [fundInvestmentName, handleDeleteFundInvestment, showDialog]);

  return {
    confirmDeleteFundInvestment,
    handleCreateFundInvestment,
    handleUpdateFundInvestment,
  };
};

export default useFundInvestment;
